import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { ScrollCollapseControls } from '~/components/ScrollCollapse/debug/ScrollCollapseControls';
import { useScrollCollapseContext } from '~/components/ScrollCollapse/hooks/useScrollCollapseContext';
import { cn } from '~/lib/cn';
import { isEnvEnabled } from '~/lib/env';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { BREADCRUMBS_PROPS } from '~/modules/artist-roster/lib/constants';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';
import { FEATURES } from '~/modules/experiments/features';
import { useFeatureFlag } from '~/modules/experiments/useFeatureFlag';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';

import { getNavLinkClassName } from '../ArtistDetails/utils';
import { ArtistHeaderDetails } from './ArtistHeaderDetails';

interface ArtistHeaderProps {
  artistData?: ArtistDetails;
  children?: ReactNode;
}

export function ArtistHeader({ artistData, children }: ArtistHeaderProps) {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const { enabled: isCampaignFeatureFlagEnabled } = useFeatureFlag(
    FEATURES.CampaignBenchmarks,
  );
  const {
    headerRef,
    scrollProgress,
    easingFunction,
    transitionDuration,
    getInterpolatedValue,
  } = useScrollCollapseContext();
  const isHeaderCollapsed = scrollProgress > 1;

  const isCampaignEnabled =
    isEnvEnabled('VITE_WMG_CAMPAIGN_BENCHMARKS_ENABLED') ||
    isCampaignFeatureFlagEnabled;

  const isTrackPreReleasesEnabled = isEnvEnabled(
    'VITE_WMG_TRACK_PRE_RELEASES_ENABLED',
  );

  const { data: tracksData } = useArtistTracksQuery({
    uaid: artistData?.uaid,
    pageNumber: 1,
    limit: 1,
    isReleased: isTrackPreReleasesEnabled ? true : undefined,
  });

  const { data: prereleasedData } = useArtistTracksQuery({
    uaid: artistData?.uaid,
    pageNumber: 1,
    limit: 1,
    isReleased: false,
  });

  const campaignLinkComponent = artistData?.uaid ? (
    <NavLink to="../campaigns" className={getNavLinkClassName}>
      {t('labels.campaigns')}
    </NavLink>
  ) : (
    <span className="cursor-default border-b pb-2xs border-transparent text-subtle">
      {t('labels.campaigns')}
    </span>
  );

  const handlePreReleaseTabsClick = () => {
    if (isTrackPreReleasesEnabled && artistData) {
      trackEvent('artistPreReleasesTabClick', {
        artist_id: artistData.partyId,
        artist_name: artistData.name,
      });
    }
  };

  return (
    <div
      className="sticky top-0 z-20 bg-gradient bg-[length:100%,auto] bg-fixed bg-no-repeat px-xl pt-lg bg-level-01/full"
      ref={headerRef}
      aria-expanded={!isHeaderCollapsed}
    >
      <ScrollCollapseControls />
      <div
        className={cn(
          'will-change-[margin-bottom,height,opacity]',
          easingFunction,
          transitionDuration,
          {
            'h-lg opacity-full': !isHeaderCollapsed,
            'pointer-events-none h-0 opacity-none': isHeaderCollapsed,
          },
        )}
        // Transition from "mb-lg" to "mb-0".
        style={{ marginBottom: getInterpolatedValue(24, 0) }}
      >
        <Breadcrumbs.Root {...BREADCRUMBS_PROPS.ROOT}>
          <Breadcrumbs.Item to="/roster" {...BREADCRUMBS_PROPS.ITEM_LINK}>
            {t('header.heading')}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>{artistData?.name}</Breadcrumbs.Item>
        </Breadcrumbs.Root>
      </div>
      <ArtistHeaderDetails artistData={artistData} />
      <div
        className={cn('flex w-full flex-col', transitionDuration, {
          'h-[28px] opacity-full': !isHeaderCollapsed,
          'pointer-events-none h-0 opacity-none': isHeaderCollapsed,
        })}
        // Transition from "mb-lg" to "12px".
        style={{ marginBottom: getInterpolatedValue(24, 12) }}
      >
        <div
          className="flex gap-lg border-b text-body-sm font-semibold border-emphasis-subtle text-subtle"
          aria-label={t('labels.artistTabs')}
        >
          <NavLink to="../releases" className={getNavLinkClassName}>
            {tracksData?.pagination
              ? t('labels.releasesCount', {
                  count: tracksData?.pagination?.total || 0,
                })
              : t('labels.releases')}
          </NavLink>
          {isTrackPreReleasesEnabled ? (
            <NavLink
              to="../pre-releases"
              className={getNavLinkClassName}
              onClick={handlePreReleaseTabsClick}
            >
              {prereleasedData?.pagination
                ? t('labels.preReleasesCount', {
                    count: prereleasedData?.pagination?.total || 0,
                  })
                : t('labels.preReleases')}
            </NavLink>
          ) : null}
          {isCampaignEnabled ? campaignLinkComponent : null}
        </div>
      </div>
      {children}
    </div>
  );
}

ArtistHeader.displayName = 'ArtistHeader';
